/* eslint-disable warp-drive/no-legacy-request-patterns */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type Group from '@linc-technologies/linced-common/models/group';
import type Store from '@linc-technologies/linced-common/services/store';

// GroupYearsService gives the groups of a year. It fetches the groups of each
// year one time, for all the group selectors.
export default class GroupYearsService extends Service {
  @service
  declare store: Store;

  // The years, other than the current year, that have their groups in the
  // store. Each change makes a new Set, so that readers update.
  @tracked loaded = new Set<number>();

  // The years whose last fetch failed. A later successful fetch removes the
  // year. Selectors can read this to show an error. groupsFor does not read
  // it, so a failure does not cause a render that fetches again.
  @tracked failed = new Set<number>();

  // The years with a fetch in progress or done. A failed fetch removes its
  // year, so the next groupsFor call for that year fetches again.
  #requested = new Set<number>();

  // groupsFor gives the groups of year that are in the store. The current
  // year's groups load at boot. For other years it starts the fetch and gives
  // [] until the groups load, as store.query gives a Promise.
  groupsFor(year: number, currentYear: number): Group[] {
    if (year !== currentYear && !this.loaded.has(year)) {
      this.#load(year);
      return [];
    }
    return this.store
      .peekAll<Group>('group')
      .filter((group) => group.year === year);
  }

  #load(year: number) {
    if (this.#requested.has(year)) {
      return;
    }
    this.#requested.add(year);
    this.store.query('group', { year }).then(
      () => {
        this.loaded = new Set(this.loaded).add(year);
        if (this.failed.has(year)) {
          const failed = new Set(this.failed);
          failed.delete(year);
          this.failed = failed;
        }
      },
      () => {
        this.#requested.delete(year);
        this.failed = new Set(this.failed).add(year);
      },
    );
  }
}
